import React from 'react';
import { Box, TextV2, TooltipV2 } from '@withjoy/joykit';
import type { ScoreBreakdown } from '@shared/utils/search';

const format = (value: number) => value.toFixed(3);

type SearchScoreDebugProps = Readonly<{
  score: ScoreBreakdown;
}>;

/**
 * Debug-only readout of a tile's search similarity, shown under the title when
 * the `showSearchScores` URL param is set. The headline number is the value that
 * gates and sorts the gallery; the tooltip breaks out the four inputs it maxes
 * over. The `concept` row is followed by the concept-breadth tiebreak (`sum`) and
 * the individual keywords feeding both, itemized at the same indent.
 *
 * A curated tile (`CURATED_THEMES_BY_SEARCH_TERM`) is flagged in the headline as
 * `📌1` — its 1-based position in the curated list, so the leading tiles should
 * read 📌1, 📌2, … left to right. The marker dims when curation was inert:
 * curation only breaks ties among perfect-similarity rows, so a curated tile
 * scoring lower keeps its rank but never got to use it.
 *
 * Deliberately untranslated — engineers only, never shown to shoppers.
 */
export const SearchScoreDebug = ({ score }: SearchScoreDebugProps) => {
  // `lexical` shows the weighted value (the 1.01 boost), not the raw one, since
  // that's what `Math.max` sees and therefore what reconciles with the headline
  // `similarity`.
  const rows: ReadonlyArray<Readonly<{ factor: ScoreBreakdown['winningFactor']; label: string; value: string }>> = [
    { factor: 'lexical', label: 'lexical', value: format(score.weightedLexical) },
    { factor: 'concept', label: 'concept', value: format(score.concept) },
    { factor: 'color', label: 'color', value: format(score.color) },
    { factor: 'custom', label: 'custom', value: format(score.custom) },
  ];

  return (
    <TooltipV2
      placement="top"
      onMouseLeaveDelay={0}
      content={
        // Two-column grid so every value lines up in its own column regardless
        // of how long the label to its left is.
        <Box
          display="grid"
          gridTemplateColumns="auto auto"
          columnGap={5}
          rowGap={1}
          textAlign="left"
        >
          {rows.map(row => (
            <React.Fragment key={row.factor}>
              <TextV2
                typographyVariant="label2"
                fontWeight={row.factor === score.winningFactor ? 700 : 400}
              >
                {`${row.label}:`}
              </TextV2>
              <TextV2
                typographyVariant="label2"
                fontWeight={row.factor === score.winningFactor ? 700 : 400}
              >
                {row.value}
              </TextV2>

              {/*
                The concept-breadth tiebreak, then the keywords behind both it
                and the concept factor, best cosine first. Terms whose cosine
                sits at or below the floor contribute nothing to the sum and are
                dimmed — the answer to "why didn't this keyword help?".
              */}
              {row.factor === 'concept' && (
                <>
                  <TextV2
                    typographyVariant="label2"
                    paddingLeft={4}
                  >
                    {'weighted sum:'}
                  </TextV2>
                  <TextV2 typographyVariant="label2">{format(score.conceptSum)}</TextV2>

                  {score.conceptTerms.map(term => (
                    <React.Fragment key={term.name}>
                      <TextV2
                        typographyVariant="label2"
                        paddingLeft={4}
                        color={term.contribution === 0 ? 'mono10' : undefined}
                      >
                        {`${term.name}:`}
                      </TextV2>
                      <TextV2
                        typographyVariant="label2"
                        color={term.contribution === 0 ? 'mono10' : undefined}
                      >
                        {format(term.cosine)}
                      </TextV2>
                    </React.Fragment>
                  ))}
                </>
              )}
            </React.Fragment>
          ))}

          {/*
            Curation sits below every factor above: it only ever breaks a tie
            those couldn't. `not applied` means the tile is in the curated list
            but didn't score a perfect match, so the rank was never consulted.
          */}
          {score.curation && (
            <>
              <TextV2
                typographyVariant="label2"
                fontWeight={score.curation.applied ? 700 : 400}
              >
                {'curated:'}
              </TextV2>
              <TextV2
                typographyVariant="label2"
                fontWeight={score.curation.applied ? 700 : 400}
              >
                {`#${(score.curation.rank ?? 0) + 1}${score.curation.applied ? '' : ' (not applied)'}`}
              </TextV2>
            </>
          )}
        </Box>
      }
    >
      <TextV2
        typographyVariant="label2"
        color="mono12"
        cursor="help"
      >
        {`${format(score.similarity)} · ${score.winningFactor}`}
        {score.curation && (
          // Dimmed when the rank didn't apply, so an inert curation reads as
          // present-but-not-in-effect rather than silently looking identical to
          // one that worked.
          <TextV2
            as="span"
            typographyVariant="label2"
            color={score.curation.applied ? 'mono14' : 'mono10'}
            fontWeight={score.curation.applied ? 700 : 400}
          >
            {` · 📌${(score.curation.rank ?? 0) + 1}`}
          </TextV2>
        )}
      </TextV2>
    </TooltipV2>
  );
};
